import { DateTime } from 'luxon'
import { Chart_Bucket } from './evaluation-chart'

/**
 * Datenpunkt für die Bucket-Bildung: Datum plus der zu summierende Wert.
 * Zeiten mappen duration (Minuten), Rechnungen amount (Euro).
 */
export interface Chart_Item {
	date: DateTime
	value: number
}

/** Deutsche Wochentagskürzel (Mo = Index 0) */
const WEEKDAY_LABELS = [ 'MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO' ]

/** Deutsche Monatskürzel (Jan = Index 0) */
const MONTH_LABELS = [ 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez' ]

/**
 * Summiert die Werte aller Items innerhalb eines Zeitraums (inklusive Grenzen)
 */
function sum_range( items: Chart_Item[], start: DateTime, end: DateTime ): number {
	return items.reduce(
		( accumulator: number, item: Chart_Item ): number => item.date >= start && item.date <= end ? accumulator + item.value : accumulator,
		0,
	)
}

/**
 * Wochenansicht: ein Bucket je übergebenem Tag. Der Aufrufer entscheidet,
 * ob Mo–Fr oder Mo–So übergeben wird.
 */
export function build_week_buckets( days: DateTime[], items: Chart_Item[] ): Chart_Bucket[] {
	return days.map( ( day ) => ( {
		label: WEEKDAY_LABELS[ day.weekday - 1 ],
		value: sum_range( items, day.startOf( 'day' ), day.endOf( 'day' ) )
	} ) )
}

/**
 * Monatsansicht: ein Bucket je Kalenderwoche des Monats,
 * an den Monatsgrenzen gekappt (analog zu render_month_data)
 */
export function build_month_buckets( start_of_month: DateTime, end_of_month: DateTime, items: Chart_Item[] ): Chart_Bucket[] {
	const buckets: Chart_Bucket[] = []
	let monday = start_of_month.startOf( 'week' )

	while ( monday <= end_of_month ) {
		const start = monday < start_of_month ? start_of_month : monday
		const end = monday.endOf( 'week' ) > end_of_month ? end_of_month : monday.endOf( 'week' )

		buckets.push( {
			label: `KW ${monday.toFormat( 'WW' )}`,
			value: sum_range( items, start, end )
		} )

		monday = monday.plus( { weeks: 1 } )
	}

	return buckets
}

/**
 * Jahresansicht: ein Bucket je Monat
 */
export function build_year_buckets( year: number, items: Chart_Item[] ): Chart_Bucket[] {
	const buckets: Chart_Bucket[] = []

	for ( let month = 1; month <= 12; month++ ) {
		const start = DateTime.fromObject( { year, month } )
		const end = start.endOf( 'month' )

		buckets.push( {
			label: MONTH_LABELS[ month - 1 ],
			value: sum_range( items, start, end )
		} )
	}

	return buckets
}

/**
 * Bonus-Jahresansicht nach Kalenderwochen: ein Bucket je ISO-Woche
 * (52 oder 53), gruppiert nach Monaten. Der Monat einer Woche wird über
 * ihren Donnerstag bestimmt (ISO-Konvention), damit jede Woche eindeutig
 * einem Monat zugeordnet ist und die Gruppen-Bänder sauber durchlaufen.
 */
export function build_year_week_buckets( year: number, items: Chart_Item[] ): Chart_Bucket[] {
	const weeks = DateTime.fromObject( { weekYear: year, weekNumber: 1, weekday: 4 } ).weeksInWeekYear
	const buckets: Chart_Bucket[] = []

	for ( let week = 1; week <= weeks; week++ ) {
		const monday = DateTime.fromObject( { weekYear: year, weekNumber: week, weekday: 1 } )
		const thursday = monday.plus( { days: 3 } )

		buckets.push( {
			label: monday.toFormat( 'WW' ),
			value: sum_range( items, monday, monday.endOf( 'week' ) ),
			group: MONTH_LABELS[ thursday.month - 1 ]
		} )
	}

	return buckets
}
